import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { InfoOutlined, Sync } from "@mui/icons-material";
import { tokens } from "../../theme";
import Cache from "../../utils/Cache";
import Header from "../../components/Header";
import IframeViewer from "../../components/IframeViewer";
import FrameFallback from "./FrameFallback";
import {
  EXPANDED_CACHE_KEY,
  FRAME_LOAD_TIMEOUT_MS,
  UI_PROXY_CONFIG,
} from "./config";
import { styles } from "./styles";

// Iframe hygiene decisions:
// - No `sandbox`: the embedded app needs allow-scripts, allow-same-origin
//   (its session cookie — without it the login loop is guaranteed),
//   allow-forms, allow-popups, allow-modals and allow-downloads. Granting all
//   of them gives no isolation and omitting any silently breaks a feature.
//   Cross-origin isolation is already enforced by the browser.
// - referrerPolicy strict-origin-when-cross-origin: the tool only ever sees
//   the portal origin, never a path. Not no-referrer, since login redirects
//   may check the Referer origin.
// - allow="fullscreen; clipboard-write": panel fullscreen and "copy link"
//   keep working inside the frame.

// Best-effort extraction of a URL/path from a postMessage payload. The frame
// is cross-origin, so this is the ONLY way to learn where the user navigated
// to inside it — and it only works if that app actually broadcasts its
// location via postMessage. Grafana is not confirmed to do this; if it
// never sends anything, liveUrl simply never updates and the fallback keeps
// pointing at the frame's starting URL (today's behavior).
const extractUrlFromMessage = (data) => {
  if (typeof data === "string") {
    return data.startsWith("http") || data.startsWith("/") ? data : null;
  }
  if (data && typeof data === "object") {
    return data.url || data.href || data.path || data.location || null;
  }
  return null;
};

const UiProxy = ({ context }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const config = UI_PROXY_CONFIG[context];
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [expanded, setExpanded] = useState(
    () => Cache.get(EXPANDED_CACHE_KEY) === "true"
  );

  // Default is the configured root URL. An optional ?path= lets links open a
  // specific page, restricted to same-origin paths so the frame can never be
  // pointed somewhere else.
  const src = useMemo(() => {
    const path = config.allowPath ? searchParams.get("path") : null;
    if (!path || !path.startsWith("/") || path.startsWith("//")) {
      return config.url;
    }
    return new URL(path, config.url).toString();
  }, [config, searchParams]);

  const grafanaOrigin = useMemo(() => {
    try {
      return new URL(config.url).origin;
    } catch {
      return null;
    }
  }, [config.url]);

  // Best-known current URL inside the frame. Starts as `src` and is updated
  // only by a postMessage from the frame's own origin (see
  // extractUrlFromMessage) — see the effect below. Handed to FrameFallback so
  // "open in new tab" (once wired up) points somewhere sensible.
  const [liveUrl, setLiveUrl] = useState(src);
  useEffect(() => {
    setLiveUrl(src);
  }, [src]);

  useEffect(() => {
    if (!grafanaOrigin) return undefined;
    const onMessage = (event) => {
      // Only ever trust messages from the embedded app's own origin.
      if (event.origin !== grafanaOrigin) return;
      const candidate = extractUrlFromMessage(event.data);
      if (!candidate) return;
      try {
        const resolved = new URL(candidate, config.url);
        // Re-check the resolved URL itself, in case a relative candidate
        // could otherwise be crafted to point elsewhere.
        if (resolved.origin === grafanaOrigin) {
          setLiveUrl(resolved.toString());
        }
      } catch {
        // Not a usable URL — ignore.
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [grafanaOrigin, config.url]);

  // Every (re)load starts from a clean loading state...
  useEffect(() => {
    setLoading(true);
    setTimedOut(false);
  }, [reloadKey, src]);

  // ...and arms a timeout that is cancelled as soon as the frame loads.
  useEffect(() => {
    if (!loading) return undefined;
    const timer = setTimeout(() => setTimedOut(true), FRAME_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [loading, reloadKey, src]);

  // Limitation: browsers fire onLoad even for X-Frame-Options / CSP blocked
  // responses and for 4xx/5xx pages, and the frame is cross-origin so its
  // document cannot be inspected. The timeout therefore only catches
  // "nothing arrived" (DNS, network, hung proxy).
  const handleLoad = () => {
    setLoading(false);
    setTimedOut(false);
  };

  // Remounting the iframe (new key) forces a fresh navigation to `src`; the
  // embedded app's own session cookie survives, so the user stays signed in.
  // This is independent of IframeViewer's maximize toggle, which must never
  // itself force a reload.
  const handleReload = () => setReloadKey((key) => key + 1);

  // IframeViewer owns the actual maximize button and Escape handling; this
  // just persists the choice the way this page already did.
  const handleExpandedChange = (next) => {
    Cache.set(EXPANDED_CACHE_KEY, String(next));
    setExpanded(next);
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.topRow}>
        <Header title={config.title} subtitle={config.subtitle} />
      </Box>
      {config.hint ? (
        <Typography sx={styles.hint(colors)}>
          <InfoOutlined sx={{ fontSize: 14 }} />
          {config.hint}
        </Typography>
      ) : null}
      <Box sx={styles.frameCard(colors)}>
        <IframeViewer
          title={config.frameTitle}
          src={src}
          iframeKey={reloadKey}
          onLoad={handleLoad}
          referrerPolicy="strict-origin-when-cross-origin"
          allow="fullscreen; clipboard-write"
          allowFullScreen
          sx={styles.iframeViewer}
          maximized={expanded}
          onMaximizedChange={handleExpandedChange}
          toolbarStart={
            <Tooltip title="Reload the embedded frame">
              <IconButton size="small" onClick={handleReload}>
                <Sync fontSize="small" />
              </IconButton>
            </Tooltip>
          }
          overlay={
            (loading || timedOut) && (
              <Box sx={styles.overlay(colors)}>
                {timedOut ? (
                  <FrameFallback
                    appName={config.appName}
                    openUrl={liveUrl}
                    openLabel={config.openLabel}
                    onRetry={handleReload}
                  />
                ) : (
                  <CircularProgress color="secondary" />
                )}
              </Box>
            )
          }
        />
      </Box>
    </Box>
  );
};

export default UiProxy;
